import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { buildApiUrl } from '../config/api';
import { useAuth } from './AuthContext';
import { useBrand } from './BrandContext';
import webChatHistoryService, {
  normalizeResearchSources,
  normalizeVerifiedSources,
  verifiedSourcesFromProvenance,
} from '../services/webChatHistoryService';
import type { EnhancedChatMessage, EnhancedChatSession, EnhancedSource, ResearchSource } from '../services/webChatHistoryService';
import { ErrorHandler, RECHERCHE_SPERRCODES, istRechercheSperre } from '../utils/errorHandler';
import { notifyResearchAccessChange } from '../services/researchAccessEvents';
import {
  DEFAULT_STAGING_LANE_SELECTION,
  ResearchStreamError,
  resolvedStagingLane,
  type AnswerProfile,
  type ResearchMetadata,
  type ResearchStreamSnapshot,
  type ResearchSuggestion,
  type ReviewAnnotation,
  type StagingLaneSelection,
  type StagingLaneVariant,
} from '../services/risResearchStream';
import {
  SessionResearchRecords,
  type SessionResearchRecord,
} from '../services/researchSessionState';
import { readyAttachmentIds, type ChatAttachmentSummary } from '../services/chatAttachments';
import { ResearchRunEndedError, ResearchRunStore, isExpectedRefusal, wakeOnReturn, type ResearchRunEnd, type ResearchRunOutcome } from '../services/researchRunStore';
import {
  INITIAL_DETAIL_STATE,
  createDetailEventApplier,
  detailRunEndState,
  detailStreamReducer,
  parseDetailStreamEvent,
  type DetailStreamState,
} from '../services/researchDetailStream';

// Types - shared with RISAssistant
// RISMode is kept for backward compatibility with old sessions
// But the app now only uses 'tooluse' mode (RISearch 2.0)
export type RISMode = 'general' | 'deep-search' | 'tooluse';

export type { EnhancedSource, ResearchSource } from '../services/webChatHistoryService';

export interface DeepRISearchCase {
  courtDecision: string;
  principle: string;
  reasoning: string;
  relevanceScore: number;
  link: string;
  norms: string[];
}

export interface DeepRISearchJurisprudence {
  summary: string;
  cases: DeepRISearchCase[];
}

export interface DeepRISearchResponse {
  answer: string;
  jurisprudence?: DeepRISearchJurisprudence;
  sources: ResearchSource[];
  metadata: {
    processingTime: number;
    baseDecision: {
      spezifizierung: boolean;
      judikatur: boolean;
    };
    jurisprudenceStats?: {
      searchResults: number;
      extractedCases: number;
      relevantCases: number;
      extractedUrteile?: number;
    };
  };
}

export interface RISLinkMetadata {
  geschaeftszahlen: Array<{
    geschaeftszahl: string;
    risUrl: string;
  }>;
  paragraphen: Array<{
    paragraph: string;
    risLink: string;
  }>;
}

export interface CaseLawDecision {
  rsNumber?: string;
  date?: string;
  court: string;
  senate?: string;
  significance: 'Leitentscheidung' | 'Grundsatzentscheidung' | 'Stützend' | 'Wendepunkt';
  risLink?: string;
  coreRuling: string;
  strategicRelevance: number;
  directApplicability: string;
  argumentationPower: number;
  confidenceScore: number;
  category: 'LEITENTSCHEIDUNG' | 'STÜTZEND' | 'INNOVATIV';
}

export interface StructuredCaseLawResponse {
  executiveSummary: {
    successProbability: string;
    strategicAssessment: string;
    keyStrengths: string[];
    potentialWeaknesses: string[];
  };
  primaryJurisprudence: CaseLawDecision[];
  supportingJurisprudence: CaseLawDecision[];
  innovativeApproaches: CaseLawDecision[];
  jurisprudenceTrend: {
    developmentDirection: string;
    temporalWeighting: string;
    senateSpecificTendencies: string;
  };
  readyToUseTextBlocks: Array<{
    type: 'OPENING' | 'SUPPORTING' | 'CLOSING';
    text: string;
    supportingRsNumbers: string[];
  }>;
  qualityMetrics: {
    totalRelevantDecisions: number;
    averageConfidenceScore: number;
    jurisprudenceConsistency: 'HIGH' | 'MEDIUM' | 'LOW';
    argumentationStrength: 'VERY_STRONG' | 'STRONG' | 'MODERATE' | 'WEAK';
  };
}

export interface RISMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  mode?: RISMode;
  /** Die Anhänge, die mit dieser Frage gegangen sind. */
  attachments?: ChatAttachmentSummary[];
  sources?: ResearchSource[];
  structuredData?: StructuredCaseLawResponse;
  verifiedSources?: EnhancedSource[];
  sourceVerificationStatus?: 'pending' | 'completed' | 'failed';
  messageId?: string;
  deepRISearchData?: DeepRISearchResponse;
  risLinkMetadata?: RISLinkMetadata;
  reviewAnnotation?: ReviewAnnotation;
  researchMetadata?: ResearchMetadata;
  // Paket 18 (RUE): Recherche-Kennung der Antwort und die eigene Bewertung (ohne Kommentar).
  researchId?: string;
  rating?: { verdict: 'UP' | 'DOWN'; category: string | null } | null;
}

/** Eine gespeicherte Nachricht des Chats als RISMessage, wie sie das Öffnen eines Chats zeigt. */
export function risMessageFromStored(msg: EnhancedChatMessage): RISMessage {
  return {
    role: msg.role === 'user' ? 'user' : 'assistant',
    content: msg.content,
    timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
    mode: (msg.metadata?.serviceType as RISMode) || 'general',
    sources: msg.metadata?.sources,
    verifiedSources: msg.metadata?.verifiedSources,
    sourceVerificationStatus: msg.metadata?.sourceVerificationStatus,
    messageId: msg.metadata?.messageId,
    risLinkMetadata: msg.metadata?.risLinkMetadata,
    reviewAnnotation: msg.metadata?.researchMetadata?.reviewAnnotation,
    researchMetadata: msg.metadata?.researchMetadata,
    attachments: msg.metadata?.attachments,
    researchId: msg.metadata?.researchId,
    rating: msg.metadata?.rating ?? null,
  };
}

const STORED_ANSWER_UNAVAILABLE = 'Die Antwort ist gespeichert, konnte aber nicht geladen werden. Bitte öffnen Sie die Recherche erneut.';

/**
 * Die gespeicherte Antwort eines Laufs, dessen Ende aus seinem Zustand stammt (Ereignisse nach der Aufbewahrung gelöscht):
 * geladen wie beim Öffnen des Chats, statt den Teiltext des Tabs als fertige Antwort zu zeigen.
 */
async function storedAnswerMessage(sessionId: string, messageId: string): Promise<RISMessage> {
  const stored = await webChatHistoryService.getChatSession(sessionId)
    .then(({ messages }) => messages.find(message => message.role !== 'user' && message.metadata?.messageId === messageId))
    .catch(() => undefined);
  if (!stored) {
    throw new ResearchStreamError('RUN_ANSWER_UNAVAILABLE', STORED_ANSWER_UNAVAILABLE,
      { answer: '', statusMessage: STORED_ANSWER_UNAVAILABLE, toolNames: [] });
  }
  return risMessageFromStored(stored);
}

const STORED_DETAIL_UNAVAILABLE = 'Die ausführliche Antwort konnte nicht geladen werden. Bitte erneut versuchen.';

/**
 * Die gespeicherte ausführliche Schicht einer Antwort, deren Lauf aus seinem Zustand endete (Ereignisse nach der
 * Aufbewahrung gelöscht): geladen wie beim Öffnen des Chats. Nur lesend; ein Start (startDetail) begänne ohne
 * gespeicherte Schicht einen neuen, abgerechneten Lauf.
 */
async function storedDetailDone(sessionId: string, messageId: string) {
  const stored = await webChatHistoryService.getChatSession(sessionId)
    .then(({ messages }) => messages.find(message => message.role !== 'user' && message.metadata?.messageId === messageId)?.metadata?.researchMetadata)
    .catch(() => undefined);
  return stored?.detailedAnswer ? parseDetailStreamEvent({ type: 'detail_done', payload: {
    detailedAnswer: stored.detailedAnswer,
    ...(stored.suggestions ? { suggestions: stored.suggestions } : {}),
    metadata: stored.reviewAnnotation ? { reviewAnnotation: stored.reviewAnnotation } : {},
  } }) : null;
}

/**
 * Der Stand am erfolgreichen Ende eines Laufs der ausführlichen Schicht. Stammt das Ende aus dem Zustand des Laufs, ist der
 * Stand höchstens ein Teiltext: die gespeicherte Schicht gilt, sonst ein Fehler mit erneutem Versuch (nie „loading“, das
 * den Chat sperrte).
 */
export async function detailEndState(
  outcome: ResearchRunOutcome<DetailStreamState>,
  sessionId: string,
  messageId: string,
): Promise<DetailStreamState> {
  if (!outcome.fromRunState) return outcome.snapshot;
  const done = await storedDetailDone(sessionId, messageId);
  return done
    ? detailStreamReducer(outcome.snapshot, done)
    : detailRunEndState(outcome.snapshot, 'RUN_ANSWER_UNAVAILABLE', STORED_DETAIL_UNAVAILABLE);
}

// Pending search state
interface PendingSearch {
  id: string;
  query: string;
  mode: RISMode;
  startedAt: Date;
  sessionId: string;
}

// Search result
export interface SearchResult {
  userMessage: RISMessage;
  assistantMessage: RISMessage;
  sessionId: string;
}

/** F13-6: eine Sperre unterscheidet sich von sonstigen Abbrüchen, damit Aufrufer keine Folgeanfragen senden. */
export type SearchOutcome = SearchResult | 'locked' | null;

export interface LiveResearchState {
  answer: string;
  statusMessage: string;
  toolNames: string[];
  sources?: ResearchSource[];
  reviewAnnotation?: ReviewAnnotation;
  verifiedSources?: EnhancedSource[];
  /** Zweischicht-Antwort: die laufende Darstellung zeigt dieselben Schichten wie die gespeicherte. */
  answerProfile?: AnswerProfile;
  detailedAnswer?: string;
  suggestions?: ResearchSuggestion[];
  /** Die Bahn, die der Server für diese Antwort gemeldet hat. */
  lane?: StagingLaneVariant;
  /** Das Modell der Bahn D, sobald der Server es meldet. */
  modelPreset?: string;
  /** Hinweis nach einem automatischen Neustart des Laufs. */
  restartNotice?: string;
}

/** Die ausführliche Schicht einer Antwort: der Chat, ihr Lauf am Server (sobald bekannt) und der Stand der Darstellung. */
export interface DetailRunRecord {
  sessionId: string;
  runId?: string;
  state: DetailStreamState;
}

/** F13-6: Zustand eines gesperrten Laufs; die Sperrseite übernimmt, sobald das Konto neu geladen ist. */
const RECHERCHE_GESPERRT = 'Die Recherche ist für dieses Konto derzeit gesperrt.';

/**
 * So vielen Läufen folgt ein Tab höchstens gleichzeitig mit eigenem Ereignisstrom (der Server erlaubt 20 je Konto). Die
 * Wiederaufnahme beim Laden der App nimmt nur so viele; den übrigen folgt der Tab, sobald ihr Chat geöffnet wird.
 */
const MAX_FOLLOWED_RUNS_PER_TAB = 5;

/** Das Modell, das eine gemeldete Bahn ausgeführt hat. */
const CONFIRMED_MODEL_LANES: Record<StagingLaneVariant, 'gemini' | 'local' | 'cortecs'> = {
  B: 'gemini',
  C: 'local',
  D: 'cortecs',
};

/** Die Bahn eines Laufs am Server als Wahl der Oberfläche: Bahn B mit ihrer Variante, sonst Bahn A. */
function laneOfRun(run: { lane?: 'A' | 'B'; variant?: StagingLaneVariant | null }): { useLaneB: boolean; selection: StagingLaneSelection } {
  return run.lane === 'B'
    ? { useLaneB: true, selection: { variant: run.variant ?? 'B' } }
    : { useLaneB: false, selection: DEFAULT_STAGING_LANE_SELECTION };
}

/**
 * Der Lauf am Server, dem dieser Tab in einem Chat folgt, mit seiner Verbindung. Ohne runId ist der Start noch nicht
 * abgeschickt (der Chat wird angelegt), mit null ist er unterwegs; ein „Abbrechen“ während des Starts merkt sich
 * stopRequested, bis die Lauf-ID da ist.
 */
interface FollowedRun {
  searchId: string;
  controller: AbortController;
  runId?: string | null;
  stopRequested: boolean;
}

// Context type
interface RISearchContextType {
  // State
  pendingSearches: PendingSearch[];
  isSearching: boolean;
  activeSearchCount: number;
  /** Wie oft ein Lauf endete, dem dieser Tab folgte (Antwort, Abbruch, Fehler); ein wegen Sperre abgewiesener Start zählt nicht. */
  endedRunCount: number;
  searchError: string | null;
  liveSearches: Record<string, LiveResearchState>;

  // Actions
  startSearch: (
    query: string,
    mode: RISMode,
    sessionId: string,
    existingMessages: RISMessage[],
    useLaneB?: boolean,
    laneSelection?: StagingLaneSelection,
    retryFailed?: boolean,
    attachments?: ChatAttachmentSummary[],
  ) => Promise<SearchOutcome>;
  cancelSearch: (sessionId: string) => void;
  /**
   * Nimmt den aktiven Lauf eines geöffneten Chats wieder auf, etwa nach einem Reload; Bahn und Variante des Laufs wählen
   * den Auswerter. Die Frage steht schon im Verlauf; ein Chat, dem dieser Tab bereits folgt, bleibt unberührt.
   */
  resumeSessionRun: (
    sessionId: string,
    runId: string,
    userMessage: RISMessage,
    run?: { lane?: 'A' | 'B'; variant?: StagingLaneVariant | null },
  ) => Promise<SearchOutcome>;
  /**
   * „Wiederholen“: ein neuer Lauf zur bestehenden Frage eines abgebrochenen oder fehlgeschlagenen Laufs, auf dessen Bahn
   * und Variante; die Frage steht weiter genau einmal im Verlauf, darunter erscheint die neue Antwort.
   */
  retryRun: (sessionId: string, runEnd: ResearchRunEnd, userMessage: RISMessage) => Promise<SearchOutcome>;
  isSessionSearching: (sessionId: string) => boolean;
  getLiveSearch: (sessionId: string) => LiveResearchState | null;
  getSessionResearch: (sessionId: string) => SessionResearchRecord<RISMessage> | null;
  acknowledgeSessionResearch: (sessionId: string) => void;
  /**
   * Beim Laden der App (Chatliste): nimmt die aktiven Rechercheläufe der Chats wieder auf, denen dieser Tab noch nicht
   * folgt, höchstens bis zur Grenze der Ereignisströme je Tab. Liefert die IDs der aufgenommenen Läufe.
   */
  resumeActiveRuns: (sessions: EnhancedChatSession[]) => string[];
  /** Die Läufe, die dieser Tab als gesehen gemeldet hat; die Seitenleiste zeigt für sie keinen Punkt „neu“ mehr. */
  seenRunIds: ReadonlySet<string>;
  /**
   * Meldet einen endgültigen Lauf des Chats als gesehen (Öffnen des Chats, Ende im geöffneten Chat); je Lauf einmal. Nicht
   * für einen Chat, der gerade gelöscht wird: sein Lauf endet durch das Löschen und verschwindet mit ihm (N-01).
   */
  reportRunSeen: (sessionId: string, runId: string) => void;
  /**
   * Die ausführliche Schicht je Antwort (Nachrichten-ID) als Lauf am Server (Ticket 09): sie belegt den Laufplatz des
   * Chats, läuft ohne geöffneten Tab weiter und steht danach an der Antwort. Das Versprechen erfüllt sich mit dem Ende.
   */
  detailRuns: Record<string, DetailRunRecord>;
  startDetailRun: (sessionId: string, messageId: string) => Promise<void>;
  /** Nimmt den Lauf der ausführlichen Schicht wieder auf, etwa nach einem Reload oder beim Öffnen des Chats. */
  resumeDetailRun: (sessionId: string, runId: string, messageId: string) => Promise<void>;
  /** Ob in diesem Chat gerade die ausführliche Schicht einer Antwort entsteht: dann wartet eine neue Frage. */
  isDetailRunning: (sessionId: string) => boolean;
  /** Der Chat, der gerade vor dem Nutzer steht (AppLayout); sein Ende meldet kein Toast, er zeigt es selbst. */
  setOpenSession: (sessionId: string | null) => void;
  clearPendingSearch: () => void;
  clearError: () => void;
}

const RISearchContext = createContext<RISearchContextType | undefined>(undefined);

interface RISearchProviderProps {
  children: ReactNode;
}

export const RISearchProvider: React.FC<RISearchProviderProps> = ({ children }) => {
  const { user } = useAuth();
  // Produktname fuer die Hinweise; als Ref, damit startSearch seine Abhaengigkeiten behaelt.
  const brand = useBrand();
  const brandName = useRef(brand.displayName);
  brandName.current = brand.displayName;
  const [pendingSearches, setPendingSearches] = useState<PendingSearch[]>([]);
  // F13-6: zählt die Enden der Läufe, denen dieser Tab folgt (Antwort, Abbruch, Fehler). Ein wegen einer Sperre
  // abgewiesener Start zählt nicht: er ändert die Chatliste nicht, und die Sperrseite übernimmt.
  const [endedRunCount, setEndedRunCount] = useState(0);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [liveSearches, setLiveSearches] = useState<Record<string, LiveResearchState>>({});
  // Was jeder Chat von der Recherche dieses Tabs zeigt; den Laufzustand halten allein der Server und der Laufspeicher.
  const recordsRef = useRef(new SessionResearchRecords<RISMessage>());
  // Alle Bahnen: Läufe am Server. Chatwechsel und neuer Chat lassen sie weiterlaufen; Abmelden und Unmount lösen nur die
  // Verbindung (disconnectFollows bricht die Leser ab, nie den Lauf).
  const runStoreRef = useRef(new ResearchRunStore());
  // Der Lauf am Server, dem dieser Tab je Chat folgt; „Abbrechen“ beendet ihn dort.
  const followsRef = useRef(new Map<string, FollowedRun>());
  const requestRunCancel = useRef((runId: string) => {
    void runStoreRef.current.cancel(runId).catch(() => {
      toast.error('Die Recherche konnte nicht abgebrochen werden. Bitte versuchen Sie es erneut.');
    });
  }).current;
  const [sessionResearch, setSessionResearch] = useState<Record<string, SessionResearchRecord<RISMessage>>>({});
  // Die Läufe, denen dieser Tab folgt oder bis zu ihrem Ende gefolgt ist. Eine Chatliste, die vor ihrem Ende
  // geladen wurde, nimmt sie nicht noch einmal auf (sonst käme die Abschlussmeldung doppelt).
  const followedRunIdsRef = useRef(new Set<string>());
  const [seenRunIds, setSeenRunIds] = useState<ReadonlySet<string>>(() => new Set());
  const seenRunIdsRef = useRef(new Set<string>());
  // N-01: Chats, deren Löschen gerade läuft (session:deleting aus der Seitenleiste), mit den Läufen, die währenddessen
  // nicht als gesehen gemeldet wurden.
  const deletingSessionsRef = useRef(new Map<string, Set<string>>());
  const [detailRuns, setDetailRuns] = useState<Record<string, DetailRunRecord>>({});
  // Die Läufe der ausführlichen Schicht, denen dieser Tab gerade folgt, je Antwort; „Abbrechen“ des Chats beendet sie am
  // Server. Ein Abbrechen während des Starts merkt sich der Eintrag, bis die Lauf-ID da ist.
  const activeDetailsRef = useRef(new Map<string, { sessionId: string; runId: string | null; stopRequested: boolean; controller: AbortController }>());
  const authenticatedUserRef = useRef(user?.id);
  // D7: neu angelegte Chats mit ihrem vorläufigen Titel, bis ihr erster Start angenommen ist. Erst dann stehen sie in der
  // Leiste (session:created) und bekommen ihren Titel; ein abgewiesener Start lässt einen leeren, gültigen Chat zurück, den
  // die Liste nicht zeigt (webChatHistoryService.getSessionsByServiceType).
  const unannouncedSessionsRef = useRef(new Map<string, string>());
  const openSessionRef = useRef<string | null>(null);
  const setOpenSession = useCallback((sessionId: string | null) => { openSessionRef.current = sessionId; }, []);
  /** UI-17: das Ende eines Laufs steht im geöffneten Chat schon unter der Frage; ein Toast nur für einen anderen Chat. */
  const shownInChat = (sessionId: string) => openSessionRef.current === sessionId && document.visibilityState === 'visible';
  const disconnectFollows = () => {
    for (const follow of followsRef.current.values()) follow.controller.abort();
    followsRef.current = new Map();
  };
  const disconnectDetails = () => {
    for (const active of activeDetailsRef.current.values()) active.controller.abort();
    activeDetailsRef.current = new Map();
  };

  useEffect(() => {
    if (authenticatedUserRef.current === user?.id) return;
    authenticatedUserRef.current = user?.id;
    disconnectFollows();
    recordsRef.current.clear();
    disconnectDetails();
    setDetailRuns({});
    unannouncedSessionsRef.current.clear();
    runStoreRef.current = new ResearchRunStore();
    followedRunIdsRef.current = new Set();
    seenRunIdsRef.current = new Set();
    deletingSessionsRef.current = new Map();
    setSeenRunIds(new Set());
    setPendingSearches([]);
    setSessionResearch({});
    setLiveSearches({});
    setSearchError(null);
  }, [user?.id]);

  // Wird der Tab wieder sichtbar oder das Netz wieder erreichbar, verbinden wartende Leser sofort neu.
  useEffect(() => {
    const detach = wakeOnReturn(() => runStoreRef.current.wake());
    return () => {
      detach();
      disconnectFollows();
      recordsRef.current.clear();
      disconnectDetails();
    };
  }, []);

  const reportRunSeen = useCallback((sessionId: string, runId: string) => {
    if (seenRunIdsRef.current.has(runId)) return;
    const skipped = deletingSessionsRef.current.get(sessionId);
    if (skipped) { skipped.add(runId); return; }
    seenRunIdsRef.current.add(runId);
    setSeenRunIds(new Set(seenRunIdsRef.current));
    void webChatHistoryService.markRunSeen(runId).catch(() => {
      console.warn('[RISearchContext] Reporting a research run as seen failed');
    });
  }, []);

  // D4: ein gelöschter Chat verliert seine Recherche in diesem Tab. Die Verbindung zu seinem Lauf endet (den Lauf bricht
  // das Löschen am Server ab), ohne Status und ohne Toast.
  useEffect(() => {
    const deleted = (event: Event) => {
      const sessionId = (event as CustomEvent<{ sessionId?: string }>).detail?.sessionId;
      if (!sessionId) return;
      followsRef.current.get(sessionId)?.controller.abort();
      followsRef.current.delete(sessionId);
      deletingSessionsRef.current.delete(sessionId);
      recordsRef.current.forget(sessionId);
      unannouncedSessionsRef.current.delete(sessionId);
      setSessionResearch(recordsRef.current.snapshot());
      setPendingSearches(prev => prev.filter(search => search.sessionId !== sessionId));
      setLiveSearches(prev => {
        if (!(sessionId in prev)) return prev;
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
    };
    // N-01: das Löschen bricht den Lauf am Server ab, bevor der Chat verschwindet; dessen Ende ist dann nicht „gesehen“.
    // Die Seitenleiste meldet den Beginn (active) und ein gescheitertes Löschen (nicht active); danach holt der Kontext die
    // Meldungen der Läufe nach, die in der Zwischenzeit geendet haben und angesehen wurden.
    const deleting = (event: Event) => {
      const { sessionId, active } = (event as CustomEvent<{ sessionId?: string; active?: boolean }>).detail ?? {};
      if (!sessionId) return;
      if (active) { deletingSessionsRef.current.set(sessionId, new Set()); return; }
      const skipped = deletingSessionsRef.current.get(sessionId);
      deletingSessionsRef.current.delete(sessionId);
      for (const runId of skipped ?? []) reportRunSeen(sessionId, runId);
    };
    window.addEventListener('session:deleted', deleted);
    window.addEventListener('session:deleting', deleting);
    return () => {
      window.removeEventListener('session:deleted', deleted);
      window.removeEventListener('session:deleting', deleting);
    };
  }, [reportRunSeen]);

  // Derived state
  const isSearching = pendingSearches.length > 0;
  const activeSearchCount = pendingSearches.length;

  // Helper to check if a specific session has an active search
  const isSessionSearching = useCallback((sessionId: string): boolean => {
    return followsRef.current.has(sessionId);
  }, [pendingSearches]);

  const getLiveSearch = useCallback((sessionId: string): LiveResearchState | null => (
    liveSearches[sessionId] || null
  ), [liveSearches]);

  const getSessionResearch = useCallback((sessionId: string): SessionResearchRecord<RISMessage> | null => (
    sessionResearch[sessionId] || null
  ), [sessionResearch]);

  const acknowledgeSessionResearch = useCallback((sessionId: string) => {
    recordsRef.current.forgetCompleted(sessionId);
    setSessionResearch(recordsRef.current.snapshot());
  }, []);

  // Helper to generate AI title for session
  /** D7: ein neuer Chat erscheint in der Leiste (session:created) und bekommt seinen Titel, einmal. */
  const announceSession = (sessionId: string, query: string) => {
    const tempTitle = unannouncedSessionsRef.current.get(sessionId);
    if (tempTitle === undefined) return;
    unannouncedSessionsRef.current.delete(sessionId);
    // Dispatch event to notify sidebar; generate the AI title asynchronously (don't block the search)
    window.dispatchEvent(new CustomEvent('session:created', { detail: { sessionId, title: tempTitle } }));
    generateSessionTitle(sessionId, query);
  };

  const generateSessionTitle = async (sessionId: string, query: string): Promise<void> => {
    try {
      console.log(`[RISearchContext] Generating AI title for session ${sessionId}...`);

      const response = await axios.post(
        buildApiUrl(`/api/ris/sessions/${sessionId}/generate-title`),
        { query },
        { withCredentials: true }
      );

      if (response.data.success && response.data.title) {
        console.log('[RISearchContext] AI title generated');
        // Dispatch event to notify sidebar to refresh
        window.dispatchEvent(new CustomEvent('session:titleUpdated', {
          detail: { sessionId, title: response.data.title }
        }));
      }
    } catch {
      console.error('[RISearchContext] AI title generation failed');
      // Non-fatal - session will keep the temporary title
    }
  };

  const startSearch = useCallback(async (
    query: string,
    mode: RISMode,
    sessionId: string,
    existingMessages: RISMessage[],
    // Die Bahn A ruht (Owner-Entscheidung 19.10.2026): false gilt nur noch für die Wiederaufnahme eines ihrer Läufe.
    useLaneB: boolean = true,
    laneSelection: StagingLaneSelection = DEFAULT_STAGING_LANE_SELECTION,
    retryFailed: boolean = false,
    attachments: ChatAttachmentSummary[] = [],
    resume?: { runId: string; userMessage: RISMessage } | { retry: ResearchRunEnd; userMessage: RISMessage },
  ): Promise<SearchOutcome> => {
    setSearchError(null);

    // Nach einem Lauf, der ohne Antwort endete: der zweite Versuch ist „Wiederholen“ zur bestehenden Frage
    // (Owner-Entscheidung 16.10.), nicht eine zweite Frage.
    const endedRecord = retryFailed && !resume ? recordsRef.current.get(sessionId) : undefined;
    if (endedRecord?.status === 'failed' && endedRecord.runEnd) {
      resume = { retry: endedRecord.runEnd, userMessage: endedRecord.userMessage };
    }
    // Eine neue Frage und „Wiederholen“ laufen stets auf der Bahn B (der Server wiederholt einen Lauf der Bahn A dort).
    const laneB = useLaneB || !(resume && 'runId' in resume);

    const searchId = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    const queryTimestamp = new Date(); // Capture timestamp at query submission
    const retryRecord = retryFailed ? recordsRef.current.get(sessionId) : undefined;
    // Steht die Frage schon mit ihrem Lauf am Server, ist der zweite Versuch ein neuer Lauf mit neuer Nachricht; dieselbe
    // Client-Nachrichten-ID lieferte nur den gescheiterten Lauf zurück.
    const canReuseFailure = retryRecord?.status === 'failed' && !retryRecord.userPersisted;
    const currentMessageId = resume?.userMessage.messageId || (canReuseFailure && retryRecord.userMessage.messageId
      ? retryRecord.userMessage.messageId
      : `${user?.id}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`);
    const userMessage: RISMessage = resume ? resume.userMessage : canReuseFailure ? retryRecord.userMessage : {
      role: 'user',
      content: query,
      timestamp: queryTimestamp,
      mode,
      messageId: currentMessageId,
      ...(attachments.length > 0 ? { attachments } : {}),
    };
    // Ein zweiter Versuch schickt dieselben Anhänge derselben Frage erneut
    // mit; der Server erkennt sie an ihrer bereits gespeicherten Nachricht.
    const pinnedAttachments = canReuseFailure ? (retryRecord.userMessage.attachments ?? []) : attachments;
    const controller = new AbortController();

    // Folgt dieser Tab dem Lauf des Chats schon, beginnt kein zweiter; jede weitere Grenze prüft allein der Server.
    if (followsRef.current.has(sessionId)) {
      toast.error('Für diese Recherche wird gerade eine Antwort erstellt.');
      return null;
    }
    const follow: FollowedRun = { searchId, controller, stopRequested: false };
    followsRef.current.set(sessionId, follow);
    const releaseFollow = () => {
      if (followsRef.current.get(sessionId) === follow) followsRef.current.delete(sessionId);
    };
    recordsRef.current.begin({
      runId: searchId,
      sessionId,
      userMessage,
      useLaneB: laneB,
      laneVariant: laneSelection.variant,
      ...(laneSelection.preset ? { modelPreset: laneSelection.preset } : {}),
      userPersisted: resume ? true : canReuseFailure ? retryRecord.userPersisted : false,
      retainedMessages: canReuseFailure ? retryRecord.retainedMessages : undefined,
    });
    setSessionResearch(recordsRef.current.snapshot());

    // Add search to pending array
    const newSearch: PendingSearch = {
      id: searchId,
      query,
      mode,
      startedAt: queryTimestamp,
      sessionId
    };
    setPendingSearches(prev => [...prev, newSearch]);

    // F13-6: ein gesperrter Lauf gibt seinen Platz frei und bleibt nicht als laufend stehen.
    const endLockedSearch = () => {
      recordsRef.current.fail(sessionId, searchId, RECHERCHE_GESPERRT);
      releaseFollow();
      setSessionResearch(recordsRef.current.snapshot());
    };

    // Determine if this is a new session (no existing messages)
    const isNewSession = existingMessages.length === 0;

    // Create the session first: the run start needs it. The sidebar shows it once the start is accepted.
    if (isNewSession && sessionId) {
      try {
        // Generate a smart temp title from the query (first 50 chars)
        const tempTitle = query.length > 50
          ? query.substring(0, 47) + '...'
          : query;

        console.log(`[RISearchContext] Creating session immediately: ${sessionId}`);

        await webChatHistoryService.createChatSessionV2({
          sessionId: sessionId,
          title: tempTitle,
          serviceType: 'ris'
        });
        unannouncedSessionsRef.current.set(sessionId, tempTitle);
      } catch (sessionError: any) {
        // F13-6: bei einer Recherchesperre kein weiterer Aufruf (keine Nutzernachricht, kein Recherche-POST) und keine
        // Meldung. Die Sperrseite kommt über das Ereignis auth:access-changed (apiClient.ts:38, AuthContext.tsx:133).
        if (istRechercheSperre(sessionError)) {
          endLockedSearch();
          setPendingSearches(prev => prev.filter(s => s.id !== searchId));
          return 'locked';
        }
        // Session might already exist (409/400) - that's OK, continue with search
        if (sessionError.response?.status !== 400 && sessionError.response?.status !== 409) {
          console.warn('[RISearchContext] Session creation failed');
        }
      }
    }

    const showSnapshot = (snapshot: ResearchStreamSnapshot) => {
      if (followsRef.current.get(sessionId) !== follow) return;
      const normalizedSources = normalizeResearchSources(snapshot.sources);
      const normalizedVerifiedSources = normalizeVerifiedSources(snapshot.verifiedSources);
      setLiveSearches(prev => ({
        ...prev,
        [sessionId]: {
          answer: snapshot.answer,
          statusMessage: snapshot.statusMessage,
          toolNames: snapshot.toolNames,
          sources: normalizedSources.length > 0 ? normalizedSources : undefined,
          reviewAnnotation: snapshot.reviewAnnotation,
          verifiedSources: normalizedVerifiedSources.length > 0 ? normalizedVerifiedSources : undefined,
          answerProfile: snapshot.answerProfile,
          detailedAnswer: snapshot.detailedAnswer,
          suggestions: snapshot.suggestions,
          lane: snapshot.lane,
          modelPreset: snapshot.modelPreset,
          restartNotice: snapshot.restartNotice,
        },
      }));
    };

    let followedRunId: string | undefined;
    try {
      // Alle Bahnen: der Server speichert Frage und Antwort; die Oberfläche startet den Lauf und verfolgt ihn nur noch.
      if (!resume || !('runId' in resume)) follow.runId = null;
      const runId = resume && 'runId' in resume ? resume.runId : (await runStoreRef.current.start(
        resume ? { sessionId, retryOf: resume.retry.runId } : {
          sessionId,
          clientMessageId: currentMessageId,
          userQuery: query,
          // Bahn B: die Wahl dieses Laufs und nur die Kennungen der Anhänge; den Fallkontext baut allein der Server. Die
          // Bahn A ruht (Owner-Entscheidung 19.10.2026); auf ihr folgt der Kontext nur noch einem wiederaufgenommenen Lauf.
          laneB: {
            variant: laneSelection.variant,
            ...(laneSelection.preset ? { modelPreset: laneSelection.preset } : {}),
            attachmentIds: readyAttachmentIds(pinnedAttachments),
          },
        },
        controller.signal,
      )).runId;
      followedRunId = runId;
      followedRunIdsRef.current.add(runId);
      follow.runId = runId;
      // „Abbrechen“ kam, während der Start unterwegs war: jetzt am Server abbrechen und dem Lauf bis zu seinem Ende folgen.
      if (follow.stopRequested) requestRunCancel(runId);
      if (!resume) announceSession(sessionId, query);
      if (!resume) {
        recordsRef.current.markUserPersisted(sessionId, searchId);
        setSessionResearch(recordsRef.current.snapshot());
      }
      console.log(`[RISearchContext] Following research run ${runId}`);
      // Bahn und Variante: der Auswerter (Hinweis auf das lokale Modell) und der Endzustand für „Wiederholen“.
      const outcome = await runStoreRef.current.follow(runId, showSnapshot, controller.signal,
        laneB ? { lane: 'B', variant: laneSelection.variant } : { lane: 'A' });
      const responseData = outcome.snapshot;
      const assistantMessageId = outcome.answerMessageId || `assistant_${runId}`;
      const storedAnswer = outcome.fromRunState ? await storedAnswerMessage(sessionId, assistantMessageId) : undefined;

      if (!storedAnswer && !responseData?.answer) {
        throw new Error('Invalid response: missing answer field');
      }
      // Paket 11: Hinweise (Kosten) des Abschlusses, sofern der Strom sie meldet.
      const finalExtras = responseData as typeof responseData & { notices?: unknown };

      const sources = normalizeResearchSources(responseData.sources);
      const explicitVerifiedSources = normalizeVerifiedSources(responseData.verifiedSources);
      const verifiedSources = explicitVerifiedSources.length > 0
        ? explicitVerifiedSources
        : verifiedSourcesFromProvenance(sources);
      const researchMetadata: ResearchMetadata | undefined = laneB ? {
        lane: resolvedStagingLane(responseData.lane),
        // Das Modell steht nur dann in den Metadaten, wenn der Server es
        // gemeldet hat -- der Wunsch der Anfrage taugt nicht als Nachweis.
        ...(responseData.lane ? { modelLane: CONFIRMED_MODEL_LANES[responseData.lane] } : {}),
        ...(responseData.lane === 'D' && responseData.modelPreset
          ? { modelPreset: responseData.modelPreset }
          : {}),
        ...(responseData.reviewAnnotation ? { reviewAnnotation: responseData.reviewAnnotation } : {}),
        ...(responseData.researchCompletion !== undefined ? { researchCompletion: responseData.researchCompletion } : {}),
        ...(responseData.researchRunId ? { researchRunId: responseData.researchRunId } : {}),
        ...(responseData.answerProfile ? { answerProfile: responseData.answerProfile } : {}),
        // Der Vermerk auf eine noch abzurufende ausführliche Schicht. Ohne ihn
        // bliebe die fertige Antwort ohne Aufklapp-Bereich stehen, obwohl die
        // Release-Linie eine zweite Schicht bereithält.
        ...(responseData.detailAvailable ? { detailAvailable: true } : {}),
        ...(responseData.detailedAnswer ? { detailedAnswer: responseData.detailedAnswer } : {}),
        ...(responseData.suggestions?.length ? { suggestions: responseData.suggestions } : {}),
      } : undefined;
      const assistantMessage: RISMessage = storedAnswer ?? {
        role: 'assistant',
        content: responseData.answer,
        timestamp: new Date(),
        mode: mode,
        sources,
        structuredData: responseData.structuredData as StructuredCaseLawResponse | undefined,
        sourceVerificationStatus: verifiedSources.length > 0
          ? responseData.sourceVerificationStatus || 'completed'
          : undefined,
        verifiedSources: verifiedSources.length > 0 ? verifiedSources : undefined,
        messageId: assistantMessageId,
        // Die Antwort gehört dem Lauf, ihre Recherche-Kennung ist ab dem Start die Lauf-ID (wie am Server gespeichert).
        researchId: runId,
        deepRISearchData: mode === 'deep-search' ? {
          answer: responseData.answer,
          jurisprudence: responseData.jurisprudence as DeepRISearchJurisprudence | undefined,
          sources,
          metadata: (responseData.metadata as DeepRISearchResponse['metadata'] | undefined) || {
            processingTime: 0,
            baseDecision: { spezifizierung: false, judikatur: false }
          }
        } : undefined,
        risLinkMetadata: responseData.risLinkMetadata as RISLinkMetadata | undefined,
        reviewAnnotation: responseData.reviewAnnotation,
        researchMetadata,
      };

      console.log(`[RISearchContext] Search completed successfully`);

      // Store result
      const result: SearchResult = {
        userMessage,
        assistantMessage,
        sessionId
      };

      recordsRef.current.complete(sessionId, searchId, assistantMessage);
      setSessionResearch(recordsRef.current.snapshot());
      // Remove this search from pending array
      setPendingSearches(prev => prev.filter(s => s.id !== searchId));
      setEndedRunCount(count => count + 1);

      // Paket 11 (KOS-12): notices of the answer, e.g. a web research skipped because of the cost budget.
      if (Array.isArray(finalExtras.notices)) {
        for (const notice of finalExtras.notices) if (typeof notice === 'string' && notice) toast.warning(notice, { duration: 15000 });
      }

      // Show toast notification with navigation
      const queryPreview = query.substring(0, 40) + (query.length > 40 ? '...' : '');

      if (!shownInChat(sessionId)) toast.success(`${brandName.current} – Recherche abgeschlossen`, {
        description: queryPreview,
        action: {
          label: 'Anzeigen',
          // AppLayout öffnet den Chat.
          onClick: () => window.dispatchEvent(new CustomEvent('session:open', { detail: { sessionId } })),
        },
        duration: 10000, // 10 seconds
      });

      // Return result directly so caller can use it without waiting for state update
      return result;

    } catch (error: any) {
      // Der Lauf endete am Server ohne Antwort. Der Chat zeigt den Status unter der Frage mit „Wiederholen“ auf derselben
      // Bahn (der Endzustand nennt sie); ein eigener Abbruch ist kein Fehler.
      const runEnd = error instanceof ResearchRunEndedError ? error.runEnd : undefined;
      // Scheitert schon der Start von „Wiederholen“, bleibt der Endzustand des alten Laufs unter der Frage stehen.
      const shownRunEnd = runEnd ?? (resume && 'retry' in resume && !followedRunId ? resume.retry : undefined);
      if (runEnd?.state === 'cancelled') {
        recordsRef.current.fail(sessionId, searchId, error.message, runEnd);
        setSessionResearch(recordsRef.current.snapshot());
        setPendingSearches(prev => prev.filter(s => s.id !== searchId));
        setEndedRunCount(count => count + 1);
        return null;
      }

      // Don't show error for user-initiated cancellations
      if (axios.isCancel(error) || error?.name === 'AbortError') {
        console.log('[RISearchContext] Search cancelled by user');
        recordsRef.current.fail(sessionId, searchId, 'Die Recherche wurde abgebrochen.');
        setSessionResearch(recordsRef.current.snapshot());
        setPendingSearches(prev => prev.filter(s => s.id !== searchId));
        setEndedRunCount(count => count + 1);
        return null;
      }

      // F13-6: eine Sperre ist ein Zustand; die Sperrseite übernimmt (auth:access-changed), kein roter Hinweis.
      if (istRechercheSperre(error)) {
        endLockedSearch();
        setPendingSearches(prev => prev.filter(s => s.id !== searchId));
        return 'locked';
      }
      // Der Recherchestrom läuft über fetch, nicht über apiClient: er meldet die Sperre als ResearchStreamError mit dem
      // Code des Servers, und das Ereignis für die Sperrseite löst erst diese Stelle aus.
      if (error instanceof ResearchStreamError && (RECHERCHE_SPERRCODES as readonly string[]).includes(error.code)) {
        notifyResearchAccessChange(403, error.code);
        endLockedSearch();
        setPendingSearches(prev => prev.filter(s => s.id !== searchId));
        return 'locked';
      }

      if (!isExpectedRefusal(error)) console.error('[RISearchContext] Search error:', error instanceof ResearchStreamError ? error.code : error?.response?.status);
      // Runde 2b (runs NOTE, gen 14): ein Start mit unklarem Ausgang (Serverfehler ohne Code, unlesbare Antwort) hat den Lauf
      // am Server vielleicht angelegt; der Chat erscheint dann wie nach einem angenommenen Start in der Leiste mit Titel.
      if (!resume && !followedRunId && error instanceof ResearchStreamError
        && (error.code === 'RUN_START_INVALID' || /^HTTP_5\d\d$/.test(error.code))) announceSession(sessionId, query);
      const errorMessage = error instanceof ResearchStreamError
        ? error.message
        : ErrorHandler.researchErrorMessage(error);
      setSearchError(errorMessage);
      recordsRef.current.fail(sessionId, searchId, errorMessage, shownRunEnd);
      setSessionResearch(recordsRef.current.snapshot());
      // Remove this search from pending array on error
      setPendingSearches(prev => prev.filter(s => s.id !== searchId));
      setEndedRunCount(count => count + 1);

      // Show error toast
      if (!shownInChat(sessionId)) toast.error(`${brandName.current} – Recherche fehlgeschlagen`, {
        description: errorMessage,
        duration: 8000,
      });

      // Der gescheiterte Lauf bleibt als Hinweis mit erneutem Versuch unter der Frage stehen (records.fail).
      return null;
    } finally {
      releaseFollow();
      setLiveSearches(prev => {
        if (followsRef.current.has(sessionId)) return prev;
        if (!(sessionId in prev)) return prev;
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
    }
  }, [user?.id]);

  const resumeSessionRun = useCallback(async (
    sessionId: string,
    runId: string,
    userMessage: RISMessage,
    run: { lane?: 'A' | 'B'; variant?: StagingLaneVariant | null } = {},
  ): Promise<SearchOutcome> => {
    // Ein Lauf, dem dieser Tab folgt oder bis zum Ende gefolgt ist, kommt nicht noch einmal (sonst doppelte Abschlussmeldung).
    if (followsRef.current.has(sessionId) || followedRunIdsRef.current.has(runId)) return null;
    const { useLaneB, selection } = laneOfRun(run);
    return startSearch(
      userMessage.content,
      userMessage.mode || 'tooluse',
      sessionId,
      [userMessage],
      useLaneB,
      selection,
      false,
      [],
      { runId, userMessage },
    );
  }, [startSearch]);

  /**
   * Die ausführliche Schicht einer Antwort über den Laufspeicher: Start oder Wiederaufnahme, dann Verfolgen bis zum Ende.
   * Eine schon gespeicherte Schicht liefert der Start sofort, ohne Lauf. Lösen der Verbindung beendet den Lauf nie.
   */
  const runDetail = useCallback(async (sessionId: string, messageId: string, resumeRunId?: string): Promise<void> => {
    if (activeDetailsRef.current.has(messageId) || (resumeRunId && followedRunIdsRef.current.has(resumeRunId))) return;
    const active = { sessionId, runId: resumeRunId ?? null, stopRequested: false, controller: new AbortController() };
    activeDetailsRef.current.set(messageId, active);
    let state = detailStreamReducer(INITIAL_DETAIL_STATE, { type: 'start' });
    const show = (next: DetailStreamState) => {
      if (activeDetailsRef.current.get(messageId) !== active) return;
      state = next;
      setDetailRuns(previous => ({ ...previous, [messageId]: { sessionId, ...(active.runId ? { runId: active.runId } : {}), state: next } }));
    };
    show(state);
    try {
      let runId = resumeRunId;
      if (!runId) {
        const started = await runStoreRef.current.startDetail({
          sessionId,
          clientMessageId: `detail_${user?.id}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
          targetMessageId: messageId,
        }, active.controller.signal);
        if ('stored' in started) {
          const done = parseDetailStreamEvent({ type: 'detail_done', payload: started.stored });
          if (done) show(detailStreamReducer(state, done));
          return;
        }
        runId = started.runId;
      }
      active.runId = runId;
      followedRunIdsRef.current.add(runId);
      // „Abbrechen“ kam, während der Start unterwegs war: jetzt am Server abbrechen und dem Lauf bis zu seinem Ende folgen.
      if (active.stopRequested) requestRunCancel(runId);
      const outcome = await runStoreRef.current.follow(runId, show, active.controller.signal, { applier: createDetailEventApplier });
      show(await detailEndState(outcome, sessionId, messageId));
    } catch (error) {
      // Nur die Verbindung endete (Abmelden, Unmount): der Lauf läuft am Server weiter und wird wieder aufgenommen.
      if (error instanceof DOMException && error.name === 'AbortError') {
        setDetailRuns(previous => {
          if (previous[messageId]?.state !== state) return previous;
          const next = { ...previous };
          delete next[messageId];
          return next;
        });
        return;
      }
      show(error instanceof ResearchStreamError
        ? detailRunEndState(state, error.code, error.message)
        : detailRunEndState(state, 'DETAIL_FAILED', 'Die ausführliche Antwort konnte nicht abgerufen werden.'));
    } finally {
      if (activeDetailsRef.current.get(messageId) === active) activeDetailsRef.current.delete(messageId);
    }
  }, [user?.id]);

  const startDetailRun = useCallback((sessionId: string, messageId: string) => runDetail(sessionId, messageId), [runDetail]);
  const resumeDetailRun = useCallback((sessionId: string, runId: string, messageId: string) => (
    runDetail(sessionId, messageId, runId)
  ), [runDetail]);
  const isDetailRunning = useCallback((sessionId: string): boolean => (
    Object.values(detailRuns).some(record => record.sessionId === sessionId && record.state.status === 'loading')
  ), [detailRuns]);

  const resumeActiveRuns = useCallback((sessions: EnhancedChatSession[]): string[] => {
    let budget = MAX_FOLLOWED_RUNS_PER_TAB - followsRef.current.size - activeDetailsRef.current.size;
    const resumed: string[] = [];
    for (const session of sessions) {
      const run = session.activeRun;
      if (budget <= 0) break;
      if (!run || !run.questionMessageId) continue;
      if (followsRef.current.has(session.sessionId) || followedRunIdsRef.current.has(run.id)) continue;
      // Die ausführliche Schicht: ihre Ziel-Nachricht steht als questionMessageId am Lauf.
      if (run.kind === 'detail') {
        budget -= 1;
        resumed.push(run.id);
        void resumeDetailRun(session.sessionId, run.id, run.questionMessageId);
        continue;
      }
      if (run.kind !== 'research') continue;
      budget -= 1;
      resumed.push(run.id);
      // Die Frage steht schon im Verlauf am Server; der Datensatz hält sie nur für Darstellung und Abschlussmeldung.
      void resumeSessionRun(session.sessionId, run.id, {
        role: 'user',
        content: run.question ?? session.title,
        timestamp: new Date(),
        mode: 'tooluse',
        messageId: run.questionMessageId,
      }, run);
    }
    return resumed;
  }, [resumeSessionRun, resumeDetailRun]);

  const retryRun = useCallback((
    sessionId: string,
    runEnd: ResearchRunEnd,
    userMessage: RISMessage,
  ): Promise<SearchOutcome> => {
    // Bahn, Variante und Modell des neuen Laufs nimmt der Server aus dem alten; die Oberfläche wählt nur den Auswerter.
    // Einen Lauf der ruhenden Bahn A wiederholt der Server auf der Bahn B mit Gemini.
    const { useLaneB, selection } = laneOfRun({ ...runEnd, lane: 'B' });
    return startSearch(
      userMessage.content,
      userMessage.mode || 'tooluse',
      sessionId,
      [userMessage],
      useLaneB,
      selection,
      false,
      [],
      { retry: runEnd, userMessage },
    );
  }, [startSearch]);

  const cancelSearch = useCallback((sessionId: string) => {
    // Der Server beendet den Lauf, auch für andere Tabs und Geräte; sein Ende (run.cancelled oder ein vorher
    // festgeschriebener Erfolg) kommt über den Ereignisstrom, dem dieser Tab weiter folgt. Ist der Start noch unterwegs,
    // bricht der Chat ab, sobald die Lauf-ID da ist; die Startanfrage selbst läuft weiter.
    const follow = followsRef.current.get(sessionId);
    if (follow && follow.runId !== undefined) {
      if (follow.runId) requestRunCancel(follow.runId);
      else follow.stopRequested = true;
      return;
    }
    // Die ausführliche Schicht, die den Laufplatz des Chats belegt: ebenso am Server.
    const detail = [...activeDetailsRef.current.values()].find(active => active.sessionId === sessionId);
    if (detail) {
      if (detail.runId) requestRunCancel(detail.runId);
      else detail.stopRequested = true;
      return;
    }
    // Der Start ist noch nicht abgeschickt (der Chat wird gerade angelegt): es entsteht kein Lauf.
    if (follow) {
      follow.controller.abort();
      recordsRef.current.fail(sessionId, follow.searchId, 'Die Recherche wurde abgebrochen.');
      followsRef.current.delete(sessionId);
    }
    setSessionResearch(recordsRef.current.snapshot());
    setPendingSearches(prev => prev.filter(search => search.sessionId !== sessionId));
    setLiveSearches(prev => {
      if (!(sessionId in prev)) return prev;
      const next = { ...prev };
      delete next[sessionId];
      return next;
    });
    setSearchError(null);
  }, []);

  const clearPendingSearch = useCallback(() => {
    disconnectFollows();
    recordsRef.current.clear();
    disconnectDetails();
    setDetailRuns({});
    followedRunIdsRef.current = new Set();
    setPendingSearches([]);
    setSessionResearch({});
    setSearchError(null);
    setLiveSearches({});
  }, []);

  const clearError = useCallback(() => {
    setSearchError(null);
  }, []);

  const value: RISearchContextType = {
    pendingSearches,
    isSearching,
    activeSearchCount,
    endedRunCount,
    searchError,
    liveSearches,
    startSearch,
    cancelSearch,
    resumeSessionRun,
    retryRun,
    isSessionSearching,
    getLiveSearch,
    getSessionResearch,
    acknowledgeSessionResearch,
    resumeActiveRuns,
    seenRunIds,
    reportRunSeen,
    detailRuns,
    startDetailRun,
    resumeDetailRun,
    isDetailRunning,
    setOpenSession,
    clearPendingSearch,
    clearError,
  };

  return (
    <RISearchContext.Provider value={value}>
      {children}
    </RISearchContext.Provider>
  );
};

export const useRISearch = (): RISearchContextType => {
  const context = useContext(RISearchContext);
  if (context === undefined) {
    throw new Error('useRISearch must be used within a RISearchProvider');
  }
  return context;
};
